/**
 * Translates the XB Wishlist app's popup, which - like Essential Preorder -
 * is injected client-side with hardcoded English strings that Translate &
 * Adapt can't reach. Unlike Essential Preorder, this app doesn't expose
 * stable data-* hooks, so we match on its BEM-style CSS classes instead
 * (xb-wishlist-*), which the app itself uses for the CSS custom properties
 * merchants theme it with, so they're a reasonably stable target.
 *
 * The item count ("0 items") is fully client-side and reactive (changes as
 * the visitor adds/removes items, no page reload), so unlike the rest of
 * this file it can't be pre-rendered by Liquid - it's parsed out of the
 * app's own text and rebuilt with Polish plural rules in JS instead.
 */
(function () {
  var translations = window.xbWishlistTranslations;
  if (!translations) return;

  function escapeHtml(text) {
    var div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  function escapeAttribute(text) {
    return String(text).replace(/"/g, '&quot;');
  }

  // Some buttons (e.g. the "create new wishlist" ones) have an <svg> icon as
  // a sibling of a bare trailing text node, not wrapped in its own element -
  // el.textContent = x would wipe the icon along with the text, so this only
  // touches the last non-empty text node instead.
  function setTrailingText(container, text) {
    if (!container) return;
    for (var i = container.childNodes.length - 1; i >= 0; i--) {
      var node = container.childNodes[i];
      if (node.nodeType === Node.TEXT_NODE && node.textContent.trim() !== '') {
        if (node.textContent.trim() !== text) {
          node.textContent = text;
        }
        return;
      }
    }
    container.appendChild(document.createTextNode(text));
  }

  // For aria-labels the app builds by wrapping a dynamic value in fixed
  // English text (e.g. "Add {product} to cart", "Share wishlist via
  // {method}") - translated once already becomes Polish text that this
  // regex won't match again, so this stays idempotent without extra state.
  function translateCapturedAriaLabel(el, regex, template, placeholderToken) {
    var label = el.getAttribute('aria-label');
    if (!label) return;
    var match = label.match(regex);
    if (!match) return;
    var newLabel = template.replace(placeholderToken, match[1]);
    if (label !== newLabel) {
      el.setAttribute('aria-label', newLabel);
    }
  }

  function polishItemsWord(count) {
    if (count === 1) return translations.itemsOne;
    var mod10 = count % 10;
    var mod100 = count % 100;
    if (mod10 >= 2 && mod10 <= 4 && !(mod100 >= 12 && mod100 <= 14)) {
      return translations.itemsFew;
    }
    return translations.itemsMany;
  }

  function translateItemsCount(el) {
    if (!translations.itemsOne || !translations.itemsFew || !translations.itemsMany) return;
    var match = el.textContent.match(/^(\d+)\s+\S+/);
    if (!match) return;
    var count = parseInt(match[1], 10);
    var text = count + ' ' + polishItemsWord(count);
    if (el.textContent !== text) {
      el.textContent = text;
    }
  }

  // Not scoped to a single modal root: the app renders more than one modal
  // variant (xb-wishlist-modal for the wishlist page, xb-wl-modal for the
  // "add to wishlist" picker opened from a listing), each with its own
  // class prefix, so every element is matched by its own specific selector
  // directly against the document instead.
  function applyTranslations() {
    document.querySelectorAll('.xb-wishlist-modal__title').forEach(function (el) {
      if (translations.title && el.textContent !== translations.title) {
        el.textContent = translations.title;
      }
    });

    document.querySelectorAll('.xb-wishlist-modal__close, .xb-wl-modal__close').forEach(function (el) {
      if (translations.close && el.getAttribute('aria-label') !== translations.close) {
        el.setAttribute('aria-label', translations.close);
      }
    });

    document
      .querySelectorAll('.xb-wishlist-grid__card-count, .xb-wishlist-picker__item-count')
      .forEach(translateItemsCount);

    // The app hardcodes the auto-created default wishlist's name to the
    // literal English word "Wishlist". Unlike every other string here, this
    // one doubles as real user data (customers can rename lists or create
    // their own), so matching on exact text is a deliberate tradeoff: a
    // customer who names their own list literally "Wishlist" gets it
    // translated too, which beats leaving every default list untranslated.
    document.querySelectorAll('.xb-wishlist-grid__card-title').forEach(function (el) {
      if (translations.defaultWishlistName && el.textContent === 'Wishlist') {
        el.textContent = translations.defaultWishlistName;
      }
    });

    document.querySelectorAll('.xb-wishlist-grid__card-default').forEach(function (el) {
      if (translations.defaultLabel && el.textContent !== translations.defaultLabel) {
        el.textContent = translations.defaultLabel;
      }
    });

    document.querySelectorAll('.xb-wishlist-grid__card-menu').forEach(function (el) {
      if (translations.moreOptions && el.getAttribute('aria-label') !== translations.moreOptions) {
        el.setAttribute('aria-label', translations.moreOptions);
      }
    });

    document.querySelectorAll('.xb-wishlist-grid__create-text').forEach(function (el) {
      if (translations.createNew && el.textContent !== translations.createNew) {
        el.textContent = translations.createNew;
      }
    });

    document.querySelectorAll('.xb-wishlist-picker__create-btn').forEach(function (el) {
      if (translations.createNew) {
        setTrailingText(el, translations.createNew);
      }
    });

    document.querySelectorAll('.xb-wishlist-grid__back-btn').forEach(function (el) {
      if (translations.backToWishlists && el.getAttribute('aria-label') !== translations.backToWishlists) {
        el.setAttribute('aria-label', translations.backToWishlists);
      }
    });

    document.querySelectorAll('.xb-wishlist-products__no-product-img').forEach(function (el) {
      if (translations.emptyWishlistImgAlt && el.getAttribute('alt') !== translations.emptyWishlistImgAlt) {
        el.setAttribute('alt', translations.emptyWishlistImgAlt);
      }
    });

    document.querySelectorAll('.xb-wishlist-products__no-product-desc > div').forEach(function (emptyDesc) {
      if (!translations.emptyWishlistBefore || !translations.emptyWishlistLinkText) return;
      var emptyLink = emptyDesc.querySelector('a');
      if (!emptyLink) return;
      var href = emptyLink.getAttribute('href') || '/collections/all';
      var newHtml =
        escapeHtml(translations.emptyWishlistBefore) +
        '<a href="' + escapeAttribute(href) + '">' + escapeHtml(translations.emptyWishlistLinkText) + '</a>';
      if (emptyDesc.innerHTML !== newHtml) {
        emptyDesc.innerHTML = newHtml;
      }
    });

    document.querySelectorAll('.xb-wishlist-grid__create-input').forEach(function (el) {
      if (translations.createFormPlaceholder && el.getAttribute('placeholder') !== translations.createFormPlaceholder) {
        el.setAttribute('placeholder', translations.createFormPlaceholder);
      }
    });

    document.querySelectorAll('.xb-wishlist-grid__create-confirm').forEach(function (el) {
      if (translations.createFormConfirm && el.textContent !== translations.createFormConfirm) {
        el.textContent = translations.createFormConfirm;
      }
    });

    document.querySelectorAll('.xb-wishlist-grid__create-cancel').forEach(function (el) {
      if (translations.createFormCancel && el.textContent !== translations.createFormCancel) {
        el.textContent = translations.createFormCancel;
      }
    });

    document.querySelectorAll('.xb-wl-modal__title').forEach(function (el) {
      if (translations.addToWishlistTitle && el.textContent !== translations.addToWishlistTitle) {
        el.textContent = translations.addToWishlistTitle;
      }
    });

    document.querySelectorAll('.xb-wishlist-picker__create-input').forEach(function (el) {
      if (translations.createFormPlaceholder && el.getAttribute('placeholder') !== translations.createFormPlaceholder) {
        el.setAttribute('placeholder', translations.createFormPlaceholder);
      }
    });

    document.querySelectorAll('.xb-wishlist-picker__create-submit').forEach(function (el) {
      if (translations.addFormSubmit && el.textContent !== translations.addFormSubmit) {
        el.textContent = translations.addFormSubmit;
      }
    });

    document.querySelectorAll('.xb-wishlist-products__share-wishlist-title').forEach(function (el) {
      if (translations.shareOn && el.textContent !== translations.shareOn) {
        el.textContent = translations.shareOn;
      }
    });

    document.querySelectorAll('.xb-wishlist-products__share-item').forEach(function (el) {
      var label = el.getAttribute('aria-label') || '';
      if (label === 'Share wishlist via Share' && translations.shareWishlist) {
        if (label !== translations.shareWishlist) el.setAttribute('aria-label', translations.shareWishlist);
        return;
      }
      if (translations.shareVia) {
        translateCapturedAriaLabel(el, /^Share wishlist via (.+)$/, translations.shareVia, '%method%');
      }
    });

    document.querySelectorAll('.xb-wishlist-products__refresh').forEach(function (el) {
      if (translations.refresh && el.getAttribute('aria-label') !== translations.refresh) {
        el.setAttribute('aria-label', translations.refresh);
      }
    });

    document.querySelectorAll('.xb-list-style__thead th:first-child').forEach(function (el) {
      if (translations.tableProductName && el.textContent !== translations.tableProductName) {
        el.textContent = translations.tableProductName;
      }
    });

    document.querySelectorAll('.xb-list-style__thead th.xb-list-style__price').forEach(function (el) {
      if (translations.tableUnitPrice && el.textContent !== translations.tableUnitPrice) {
        el.textContent = translations.tableUnitPrice;
      }
    });

    document.querySelectorAll('.xb-list-style__add-to-cart').forEach(function (el) {
      if (translations.addToCart) {
        setTrailingText(el, translations.addToCart);
      }
      if (translations.addProductToCartAria) {
        translateCapturedAriaLabel(el, /^Add (.+) to cart$/, translations.addProductToCartAria, '%product%');
      }
    });

    document.querySelectorAll('.xb-product-menu__trigger').forEach(function (el) {
      if (translations.wishlistActions && el.getAttribute('aria-label') !== translations.wishlistActions) {
        el.setAttribute('aria-label', translations.wishlistActions);
      }
    });

    document.querySelectorAll('.xb-product-menu__item:not(.xb-product-menu__item--danger)').forEach(function (el) {
      if (translations.moveItem) {
        setTrailingText(el, translations.moveItem);
      }
    });

    document.querySelectorAll('.xb-product-menu__item--danger').forEach(function (el) {
      if (translations.removeItem) {
        setTrailingText(el, translations.removeItem);
      }
    });

    document.querySelectorAll('.xb-wishlist-grid__dropdown-item:not(.xb-wishlist-grid__dropdown-item--delete)').forEach(function (el) {
      if (translations.rename) {
        setTrailingText(el, translations.rename);
      }
    });

    document.querySelectorAll('.xb-wishlist-grid__dropdown-item--delete').forEach(function (el) {
      if (translations.deleteLabel) {
        setTrailingText(el, translations.deleteLabel);
      }
    });

    document.querySelectorAll('.xb-wishlist-products__caution').forEach(function (el) {
      if (
        !translations.saveWishlistBefore ||
        !translations.saveWishlistLogin ||
        !translations.saveWishlistOr ||
        !translations.saveWishlistRegister ||
        !translations.saveWishlistAfter
      ) {
        return;
      }
      var links = el.querySelectorAll('a');
      if (links.length < 2) return;
      var loginHref = links[0].getAttribute('href') || '/account/login';
      var registerHref = links[1].getAttribute('href') || '/account/register';
      var newHtml =
        escapeHtml(translations.saveWishlistBefore) +
        '<a href="' + escapeAttribute(loginHref) + '">' + escapeHtml(translations.saveWishlistLogin) + '</a>' +
        escapeHtml(translations.saveWishlistOr) +
        '<a href="' + escapeAttribute(registerHref) + '">' + escapeHtml(translations.saveWishlistRegister) + '</a>' +
        escapeHtml(translations.saveWishlistAfter);
      if (el.innerHTML !== newHtml) {
        el.innerHTML = newHtml;
      }
    });
  }

  // The app re-renders reactively (see comment above), so a single user
  // action can fire dozens of DOM mutations in one burst. Running the full
  // applyTranslations scan synchronously for every single mutation record
  // made the wishlist picker noticeably freeze the page, so batch bursts
  // into at most one scan per animation frame instead.
  var applyScheduled = false;
  function scheduleApplyTranslations() {
    if (applyScheduled) return;
    applyScheduled = true;
    requestAnimationFrame(function () {
      applyScheduled = false;
      applyTranslations();
    });
  }

  var observer = new MutationObserver(scheduleApplyTranslations);
  observer.observe(document.body, { childList: true, subtree: true, characterData: true });

  applyTranslations();
})();
